import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Bot, Copy, Pencil, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { RichText, isBlank } from '../../components/rich-text';
import {
  Badge,
  Button,
  buttonClassName,
  Card,
  CardBody,
  CardHeader,
  CollapsibleCard,
  CaseStatusBadge,
  ConfirmDialog,
  CopyKey,
  ErrorState,
  ImageZoom,
  LabelChip,
  PriorityBadge,
  SeverityBadge,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { CaseAutomationCard } from '../automation/case-links/CaseAutomationCard';
import { useDeleteTestCase, useDuplicateTestCase, useTestCase } from './api';

/** One property of the case: a label above its value. */
const Detail = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <dt className="text-[12px] uppercase tracking-wide text-ink-subtle">{label}</dt>
    <dd className="mt-1 flex items-center">{children}</dd>
  </div>
);

/** One of the written fields, rendered from its Markdown. */
const Section = ({ label, value }: { label: string; value: string | null }) => {
  const { t } = useT();
  return (
    <section>
      <h3 className="text-[12px] uppercase tracking-wide text-ink-subtle">{label}</h3>
      <div className="mt-1.5">
        {isBlank(value) ? (
          <p className="text-[13px] text-ink-subtle">{t('common.empty')}</p>
        ) : (
          <RichText value={value ?? ''} className="max-w-5xl" />
        )}
      </div>
    </section>
  );
};

export const TestCaseDetailPage = ({
  projectCode,
  caseKey,
}: {
  projectCode: string;
  caseKey: string;
}) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { dateTime } = useFormat();
  const { automation } = useEnumLabels();
  const { errorText } = useErrorText();
  const { data: testCase, isPending, error, refetch } = useTestCase(projectCode, caseKey);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const duplicate = useDuplicateTestCase(projectCode);
  const remove = useDeleteTestCase(projectCode);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending || !testCase) return <SkeletonRows rows={5} className="p-0" />;

  const onDuplicate = async () => {
    try {
      const copy = await duplicate.mutateAsync(testCase.id);
      toast.success(t('cases.duplicated', { key: copy.caseKey }));
      await navigate({
        to: '/projects/$projectCode/test-cases/$caseKey',
        params: { projectCode, caseKey: copy.caseKey },
      });
    } catch (duplicateError) {
      toast.error(errorText(duplicateError, 'cases.duplicateFailed'));
    }
  };

  const confirmDelete = async () => {
    try {
      await remove.mutateAsync(testCase.id);
      toast.success(t('cases.deleted', { key: testCase.caseKey }));
      await navigate({ to: '/projects/$projectCode/test-cases', params: { projectCode } });
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'cases.deleteFailed'));
    }
  };

  return (
    <>
      {/* The identity of the case — key, priority, severity, status, labels —
          belongs with the rest of its properties in Details, not scattered
          next to the title. */}
      <PageHeader
        title={testCase.title}
        actions={
          <>
            {/* A button, not a quiet link: leaving the record is a real
                action and has to look like one. */}
            <Link
              to="/projects/$projectCode/test-cases"
              params={{ projectCode }}
              className={buttonClassName('secondary')}
            >
              <ArrowLeft className="size-3.5" />
              {t('cases.backToList')}
            </Link>
            {can('case:write') ? (
              <>
                <Button onClick={() => void onDuplicate()} loading={duplicate.isPending}>
                  <Copy className="size-3.5" />
                  {t('common.duplicate')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    void navigate({
                      to: '/projects/$projectCode/test-cases/$caseKey/edit',
                      params: { projectCode, caseKey: testCase.caseKey },
                    })
                  }
                >
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              </>
            ) : null}
            {can('case:delete') ? (
              <Button
                variant="ghost"
                onClick={() => setDeleteOpen(true)}
                aria-label={t('cases.deleteAction')}
              >
                <Trash2 className="size-3.5 text-fail" />
              </Button>
            ) : null}
          </>
        }
      />

      {/* Folded on a first visit — the properties are reference material while
          the scenario below is what the page is opened for — and from then on
          the way the reader left it. */}
      <CollapsibleCard title={t('cases.details')} rememberAs="caseDetails">
        <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
          <Detail label={t('cases.columnKey')}>
            {/* The key copies a link: it is what people quote to each other. */}
            <CopyKey path={`/projects/${projectCode}/test-cases/${testCase.caseKey}`}>
              {testCase.caseKey}
            </CopyKey>
          </Detail>
          <Detail label={t('common.status')}>
            <CaseStatusBadge status={testCase.status} />
          </Detail>
          <Detail label={t('labels.title')}>
            {testCase.labels.length === 0 ? (
              <span className="text-[13px] text-ink-subtle">{t('common.empty')}</span>
            ) : (
              <span className="flex flex-wrap gap-1">
                {testCase.labels.map((label) => (
                  <LabelChip key={label.id} label={label} />
                ))}
              </span>
            )}
          </Detail>
          <Detail label={t('common.environment')}>
            <span className="text-[13px] text-ink">
              {testCase.environment?.name ?? t('environments.none')}
            </span>
          </Detail>
          <Detail label={t('common.layer')}>
            <span className="text-[13px] text-ink">{testCase.layer?.name ?? t('layers.none')}</span>
          </Detail>
          <Detail label={t('common.module')}>
            <span className="text-[13px] text-ink">
              {testCase.module?.name ?? t('modules.none')}
            </span>
          </Detail>
          <Detail label={t('enum.priorityLabel')}>
            <PriorityBadge priority={testCase.priority} />
          </Detail>
          <Detail label={t('enum.severityLabel')}>
            <SeverityBadge severity={testCase.severity} />
          </Detail>
          <Detail label={t('cases.automated')}>
            {testCase.isAutomated ? (
              <Badge className="bg-accent-soft text-accent-fg">
                <Bot className="size-3 shrink-0" aria-hidden="true" />
                {automation(true)}
              </Badge>
            ) : (
              <span className="text-[13px] text-ink-muted">{automation(false)}</span>
            )}
          </Detail>
          <Detail label={t('common.author')}>
            <span className="text-[13px] text-ink">
              {testCase.createdBy?.name ?? t('common.empty')}
            </span>
          </Detail>
          <Detail label={t('common.created')}>
            <span className="text-[13px] text-ink">{dateTime(testCase.createdAt)}</span>
          </Detail>
          <Detail label={t('common.updated')}>
            <span className="text-[13px] text-ink">{dateTime(testCase.updatedAt)}</span>
          </Detail>
        </dl>
      </CollapsibleCard>

      {/* The written part of the case: its own block, so the properties above
          stay scannable and the prose has room. */}
      <Card>
        <CardHeader title={t('cases.contentTitle')} />
        <CardBody className="flex flex-col gap-5">
          <Section label={t('common.description')} value={testCase.description} />
          <Section label={t('cases.preconditions')} value={testCase.preconditions} />
          <Section label={t('cases.stepData')} value={testCase.testData} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('cases.stepsCount', { count: testCase.steps.length })} />
        {testCase.steps.length === 0 ? (
          <CardBody>
            <p className="text-[13px] text-ink-subtle">{t('cases.noSteps')}</p>
          </CardBody>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th className="w-12">#</Th>
                  <Th>{t('cases.stepAction')}</Th>
                  <Th>{t('cases.stepExpected')}</Th>
                  <Th className="w-32">{t('cases.stepScreenshot')}</Th>
                </tr>
              </thead>
              <tbody>
                {testCase.steps.map((step) => (
                  <tr key={step.id}>
                    <Td className="align-top tabular-nums text-ink-subtle">{step.position}</Td>
                    <Td className="align-top">
                      <RichText value={step.action} />
                    </Td>
                    <Td className="align-top">
                      {isBlank(step.expectedResult) ? (
                        <span className="text-ink-subtle">{t('common.empty')}</span>
                      ) : (
                        <RichText value={step.expectedResult ?? ''} />
                      )}
                    </Td>
                    <Td className="align-top">
                      {step.screenshot ? (
                        <ImageZoom
                          src={step.screenshot.url}
                          alt={step.screenshot.fileName}
                          size="sm"
                        />
                      ) : (
                        <span className="text-ink-subtle">{t('common.empty')}</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      <CaseAutomationCard caseKey={testCase.caseKey} canEdit={can('case:write')} />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('cases.deleteTitle', { key: testCase.caseKey })}
        description={t('cases.deleteHint')}
        confirmLabel={t('cases.deleteAction')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
